import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import { pdfReportService } from '../services/pdfReport';
import { Query, PlatformResult, VisibilityScoreResult, Business, QueryResult } from '../types/api';
import Header from './Header';
import './AnalysisResults.css';

interface LocationState {
  email: string;
  formData: Record<string, string>;
}

// Helper function to replace all @question_id patterns with user responses
const replaceTemplateVariables = (text: string, formData: Record<string, string>): string => {
  let processedText = text;
  
  // Find all @question_id patterns in the text
  const templateMatches = text.match(/@[a-zA-Z_][a-zA-Z0-9_]*/g);
  
  if (templateMatches) {
    templateMatches.forEach(match => {
      // Remove the @ symbol to get the question_id
      const questionId = match.substring(1);
      
      // Replace with the user's response if it exists
      if (formData[questionId]) {
        processedText = processedText.replace(new RegExp(match.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), formData[questionId]);
      } else {
        console.warn(`No user response found for question_id: ${questionId}`);
        // Keep the original @question_id if no replacement is found
      }
    });
  }
  
  return processedText;
};

// Helper function to extract business information from form data
const extractBusinessInfo = (formData: Record<string, string>): Business => {
  // Priority order for business name fields
  const businessNameFields = ['company_name', 'business_name', 'name'];
  const businessName = businessNameFields.find(field => formData[field]) || '';
  
  // Priority order for business domain/website fields
  const businessDomainFields = ['company_website', 'website', 'domain', 'business_website'];
  const businessDomain = businessDomainFields.find(field => formData[field]) || '';
  
  // Clean up domain (remove http/https if present)
  let cleanDomain = formData[businessDomain] || '';
  if (cleanDomain) {
    cleanDomain = cleanDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');
  }
  
  const business: Business = {
    name: formData[businessName] || '',
    domain: cleanDomain,
    alt_names: formData.alt_names ? formData.alt_names.split(',').map(n => n.trim()) : []
  };
  
  console.log('Business extracted from form data:', {
    formData: Object.keys(formData),
    businessNameField: businessName,
    businessDomainField: businessDomain,
    business
  });
  
  return business;
};

// Helper function to sort platforms in consistent order
const sortPlatformResults = (platforms: PlatformResult[]): PlatformResult[] => {
  const platformOrder = ['openai', 'chatgpt', 'gemini', 'google', 'grok', 'x', 'perplexity', 'claude', 'anthropic'];
  
  const normalizePlatformName = (platform: string): string => {
    const normalizedMap: Record<string, string> = {
      'chatgpt': 'openai',
      'openai': 'openai',
      'claude': 'claude',
      'anthropic': 'claude',
      'gemini': 'gemini',
      'google': 'gemini',
      'grok': 'grok',
      'x': 'grok',
      'perplexity': 'perplexity'
    };
    return normalizedMap[platform.toLowerCase()] || platform.toLowerCase();
  };
  
  return [...platforms].sort((a, b) => {
    const normalizedA = normalizePlatformName(a.platform);
    const normalizedB = normalizePlatformName(b.platform);
    
    const indexA = platformOrder.indexOf(normalizedA);
    const indexB = platformOrder.indexOf(normalizedB);
    
    // If both found in order array, sort by position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    
    // If only A found, A comes first
    if (indexA !== -1) return -1;
    
    // If only B found, B comes first
    if (indexB !== -1) return 1;
    
    // If neither found, sort alphabetically
    return a.platform.localeCompare(b.platform);
  });
};

const AnalysisResults: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  const hasStartedAnalysis = useRef(false);
  
  const [queries, setQueries] = useState<Query[]>([]);
  const [platformResults, setPlatformResults] = useState<PlatformResult[]>([]);
  const [currentPlatform, setCurrentPlatform] = useState<string | null>(null);
  const [overallScore, setOverallScore] = useState<number | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [businessInfo, setBusinessInfo] = useState<Business | null>(null);
  const [expandedPlatforms, setExpandedPlatforms] = useState<Set<string>>(new Set());
  const [availablePlatforms, setAvailablePlatforms] = useState<string[]>([]);
  const [runningQueries, setRunningQueries] = useState<Record<string, string[]>>({});
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const email = state?.email;
  const formData = state?.formData;

  useEffect(() => {
    // Redirect if no email or form data
    if (!email || !formData) {
      navigate('/', { replace: true });
      return;
    }

    // Prevent multiple initializations
    if (hasStartedAnalysis.current) {
      return;
    }
    hasStartedAnalysis.current = true;

    // Set up business info from form data with dynamic question ID mapping
    const business = extractBusinessInfo(formData);
    setBusinessInfo(business);

    // Start the analysis (will determine platforms based on available queries)
    startAnalysis(business);
  }, [email, formData, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const startAnalysis = async (business: Business) => {
    try {
      // Step 1: Fetch all active queries from database
      console.log('Fetching queries from database...');
      const queriesData = await apiService.getQueries(undefined, true); // Get only active queries
      const availableQueries = queriesData.items || [];
      setQueries(availableQueries);

      if (availableQueries.length === 0) {
        throw new Error('No active queries available for analysis');
      }

      console.log(`Found ${availableQueries.length} active queries in database:`, availableQueries);
      console.log('Form data available for template replacement:', formData);

      // Step 2: Group queries by platform and determine which platforms have queries
      // Note: ai_platform is now an array, so we need to handle multiple platforms per query
      const queriesByPlatform = availableQueries.reduce((acc, query) => {
        // Each query can now support multiple platforms
        query.ai_platform.forEach(platform => {
          if (!acc[platform]) {
            acc[platform] = [];
          }
          acc[platform].push(query);
        });
        return acc;
      }, {} as Record<string, Query[]>);

      const platformsWithQueries = Object.keys(queriesByPlatform);
      setAvailablePlatforms(platformsWithQueries);

      console.log('Platforms with queries:', platformsWithQueries);
      console.log('Queries by platform:', queriesByPlatform);

      // Initialize platform results only for platforms that have queries
      const initialResults: PlatformResult[] = platformsWithQueries.map(platform => ({
        platform: platform as any,
        status: 'pending'
      }));
      setPlatformResults(initialResults);

      // Step 3: Set all platforms to running status
      setPlatformResults(prev => prev.map(p => ({
        ...p,
        status: 'running' as const,
        totalQueries: queriesByPlatform[p.platform]?.length || 0
      })));

      // Step 4: Run analysis for ALL platforms and queries in parallel
      console.log('Running analysis for all platforms in parallel...');
      
      const platformPromises = platformsWithQueries.map(async (platform) => {
        setCurrentPlatform(platform);
        const queriesForThisPlatform = queriesByPlatform[platform];
        
        console.log(`Starting analysis for ${platform} with ${queriesForThisPlatform.length} queries`);
        
        // Initialize running queries for this platform
        setRunningQueries(prev => ({
          ...prev,
          [platform]: queriesForThisPlatform.map(q => q.query_id)
        }));
        
        try {
          // Run all queries for this platform in parallel
          const queryPromises = queriesForThisPlatform.map(async (query) => {
            // Replace template variables in query text
            let processedQueryText = query.query_text;
            
            // Replace all @question_id patterns with user responses
            processedQueryText = replaceTemplateVariables(processedQueryText, formData);

            console.log(`Executing query for ${platform}: ${processedQueryText}`);
            
            try {
              const result = await apiService.analyzeVisibility({
                email: email, // Required for authentication
                ai_platform: platform as any,
                query_text: processedQueryText,
                business: business,
                top_k: 10
              });

              // Remove this query from running queries
              setRunningQueries(prev => ({
                ...prev,
                [platform]: prev[platform]?.filter(id => id !== query.query_id) || []
              }));

              const queryResult = {
                query: query,
                result: result,
                found: result.analytics.found,
                confidence: result.analytics.confidence,
                rank: result.analytics.rank || null
              };

              // ✨ Update platform results immediately with this individual query result
              setPlatformResults(prev => prev.map(p => {
                if (p.platform === platform) {
                  const existingQueries = p.queries || [];
                  const updatedQueries = [...existingQueries, queryResult];
                  const foundCount = updatedQueries.filter(q => q.found).length;
                  const totalConfidence = updatedQueries.reduce((sum, q) => sum + (q.confidence || 0), 0);
                  const avgConfidence = foundCount > 0 ? totalConfidence / foundCount : 0;
                  
                  return {
                    ...p,
                    queries: updatedQueries,
                    foundCount: foundCount,
                    found: foundCount > 0,
                    confidence: avgConfidence
                  };
                }
                return p;
              }));

              return queryResult;

            } catch (queryErr: any) {
              console.error(`Error analyzing query "${processedQueryText}" on ${platform}:`, queryErr);
              
              // Remove this query from running queries even on error
              setRunningQueries(prev => ({
                ...prev,
                [platform]: prev[platform]?.filter(id => id !== query.query_id) || []
              }));

              const queryResult = {
                query: query,
                result: null,
                found: false,
                confidence: 0,
                rank: null,
                error: queryErr.message
              };

              // ✨ Update platform results immediately even for errors
              setPlatformResults(prev => prev.map(p => {
                if (p.platform === platform) {
                  const existingQueries = p.queries || [];
                  const updatedQueries = [...existingQueries, queryResult];
                  const foundCount = updatedQueries.filter(q => q.found).length;
                  const totalConfidence = updatedQueries.reduce((sum, q) => sum + (q.confidence || 0), 0);
                  const avgConfidence = foundCount > 0 ? totalConfidence / foundCount : 0;
                  
                  return {
                    ...p,
                    queries: updatedQueries,
                    foundCount: foundCount,
                    found: foundCount > 0,
                    confidence: avgConfidence
                  };
                }
                return p;
              }));

              return queryResult;
            }
          });

          // Wait for all queries for this platform to complete
          const queryResults = await Promise.all(queryPromises);
          
          console.log(`✅ Completed analysis for ${platform}: ${queryResults.filter(q => q.found).length}/${queriesForThisPlatform.length} queries found`);
          
          // Clear running queries for this platform
          setRunningQueries(prev => ({
            ...prev,
            [platform]: []
          }));
          
          // Update this platform's status to completed (queries are already updated in real-time)
          setPlatformResults(prev => prev.map(p => 
            p.platform === platform 
              ? { ...p, status: 'completed' as const }
              : p
          ));

          // Return the current state for this platform (used for score calculation)
          return {
            platform: platform as any,
            status: 'completed' as const,
            found: queryResults.some(q => q.found),
            confidence: queryResults.filter(q => q.found).reduce((sum, q) => sum + (q.confidence || 0), 0) / (queryResults.filter(q => q.found).length || 1),
            queries: queryResults,
            foundCount: queryResults.filter(q => q.found).length,
            totalQueries: queriesForThisPlatform.length
          };

        } catch (err: any) {
          console.error(`❌ Error analyzing platform ${platform}:`, err);
          
          const platformResult: PlatformResult = {
            platform: platform as any,
            status: 'error',
            error: err.message || 'Analysis failed',
            queries: [],
            found: false,
            confidence: 0,
            foundCount: 0,
            totalQueries: queriesForThisPlatform.length
          };

          // Clear running queries for this platform on error
          setRunningQueries(prev => ({
            ...prev,
            [platform]: []
          }));

          // Update this platform's status to error immediately
          setPlatformResults(prev => prev.map(p => 
            p.platform === platform 
              ? platformResult
              : p
          ));

          return platformResult;
        }
      });

      // Wait for ALL platforms to complete
      const allResults = await Promise.all(platformPromises);

      // Step 3: Calculate overall score based on all results
      setCurrentPlatform(null);
      console.log('Calculating overall score...');
      
      try {
        // Create analytics list from all successful results
        const analytics_list: any[] = [];
        
        allResults.forEach(platformResult => {
          if (platformResult.status === 'completed' && platformResult.queries) {
            platformResult.queries.forEach(queryResult => {
              if (queryResult.result && queryResult.found) {
                analytics_list.push({
                  analytics: queryResult.result.analytics,
                  query_id: queryResult.query.query_id,
                  weight: 1.0
                });
              }
            });
          }
        });

        if (analytics_list.length > 0) {
          const scoreResult: VisibilityScoreResult = await apiService.calculateVisibilityScore({
            analytics_list,
            business,
            max_rank: 10
          });
          
          setOverallScore(Math.round(scoreResult.score)); // Use the score as-is from API
        } else {
          // Calculate a basic score based on found percentage
          const totalQueries = allResults.reduce((sum, result) => sum + (result.totalQueries || 0), 0);
          const successfulFinds = allResults.reduce((sum, result) => sum + (result.foundCount || 0), 0);
          const foundPercentage = totalQueries > 0 ? (successfulFinds / totalQueries) * 100 : 0;
          setOverallScore(Math.round(foundPercentage / 10)); // Convert to 0-10 scale
        }
      } catch (err: any) {
        console.error('Error calculating score:', err);
        // Calculate fallback score based on platform success
        const successfulPlatforms = allResults.filter(r => r.found).length;
        const fallbackScore = Math.round((successfulPlatforms / platformsWithQueries.length) * 10);
        setOverallScore(fallbackScore);
      }

      setIsComplete(true);
      console.log('Analysis complete!', { allResults, overallScore });

    } catch (err: any) {
      console.error('Analysis error:', err);
      setError(err.message || 'Failed to run analysis');
    }
  };

  const handleDownloadReport = async () => {
    try {
      if (!businessInfo || overallScore === null) {
        alert('Report data is not ready yet. Please wait for the analysis to complete.');
        return;
      }

      setIsGeneratingReport(true);

      // Use the same sorted platform results for PDF as displayed on screen
      const sortedPlatformResults = sortPlatformResults(platformResults);

      const processedPlatformResults = sortedPlatformResults.map(platformResult => ({
        ...platformResult,
        queries: platformResult.queries?.map(queryResult => ({
          ...queryResult,
          processedQueryText: replaceTemplateVariables(queryResult.query.query_text, formData),
          response: queryResult.result?.raw_response || null,
          found: queryResult.found,
          confidence: queryResult.confidence,
          rank: queryResult.rank,
          error: queryResult.error
        })) || []
      }));

      const reportData = {
        businessInfo,
        formData: formData || {},
        platformResults: processedPlatformResults,
        overallScore,
        isComplete,
        totalQueries: platformResults.reduce((sum, result) => sum + (result.totalQueries || 0), 0),
        totalFound: platformResults.reduce((sum, result) => sum + (result.foundCount || 0), 0),
        analysisDate: new Date().toISOString()
      };

      // Add a small delay to show the loading state
      await new Promise(resolve => setTimeout(resolve, 500));
      
      await pdfReportService.generateReport(reportData);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate PDF report. Please try again.');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleSearchAgain = () => {
    navigate('/', { replace: true });
  };

  const togglePlatformDetails = (platform: string) => {
    setExpandedPlatforms(prev => {
      const newSet = new Set(prev);
      if (newSet.has(platform)) {
        newSet.delete(platform);
      } else {
        newSet.add(platform);
      }
      return newSet;
    });
  };

  const getPlatformStatus = (result: PlatformResult) => {
    if (result.status === 'error') return 'Not Found';
    if (result.status === 'running') return 'Running...';
    if (result.status === 'pending') return 'Pending';
    if (result.status === 'completed') {
      if (result.foundCount && result.foundCount > 0) {
        return result.foundCount > result.totalQueries! / 2 ? 'Great' : 'Good';
      }
      return 'Not Found';
    }
    return 'Poor';
  };

  const getPlatformStatusColor = (result: PlatformResult) => {
    if (result.status === 'error' || (result.status === 'completed' && !result.found)) return '#ef4444';
    if (result.status !== 'completed') return '#6b7280';
    if (result.foundCount && result.foundCount > result.totalQueries! / 2) return '#10b981';
    if (result.foundCount && result.foundCount > 0) return '#f59e0b';
    return '#ef4444';
  };

  if (error) {
    return (
      <div className="analysis-results error">
        <div className="error-container">
          <h2>Analysis Failed</h2>
          <p>{error}</p>
          <button onClick={handleSearchAgain} className="search-again-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="analysis-results">
      <Header />
      <div className="results-container">
        <div className="results-wrapper">
          {/* Header Section - matching screenshot */}
          <div className="results-header">
            <div className={`status-badge ${isComplete ? 'complete' : 'running'}`}>
              {isComplete ? 'Analysis Complete' : 'Analysis Running...'}
            </div>
            
            <h1 className="results-title">Your AI Search Visibility Report</h1>
            
            {businessInfo && (
              <div className="business-summary">
                <span>Here's how </span>
                <span className="business-name">{businessInfo.name}</span>
                {formData.service && (
                  <span className="service-badge">({formData.service})</span>
                )}
                <span> appears in AI results for </span>
                <span className="location-badge">{formData.person_city}</span>
              </div>
            )}

            <div className="action-buttons">
              <button 
                onClick={handleDownloadReport} 
                className="download-btn"
                disabled={isGeneratingReport || !isComplete || overallScore === null}
              >
                {isGeneratingReport ? (
                  <>
                    <span className="loading-spinner small" style={{ marginRight: '8px' }}></span>
                    Generating...
                  </>
                ) : (
                  'Download Report'
                )}
              </button>
              <button onClick={handleSearchAgain} className="search-again-btn">
                Search Again
              </button>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="content-grid">
            {/* Left Column - Overall Score */}
            <div className="score-section">
              <h2>Overall Score</h2>
              {overallScore !== null ? (
                <div className="score-display">
                  <div 
                    className="score-circle"
                    style={{
                      '--progress-angle': `${(overallScore / 10) * 360}deg`
                    } as React.CSSProperties}
                  >
                    <span className="score-number">{overallScore}</span>
                    <span className="score-total">out of 10</span>
                  </div>
                  <p className="score-description">
                    {overallScore >= 8 ? 'Excellent visibility in AI searches' :
                     overallScore >= 6 ? 'Good visibility in AI searches' :
                     overallScore >= 4 ? 'Moderate visibility in AI searches' :
                     overallScore >= 2 ? 'Limited visibility in AI searches' :
                     'Poor visibility in AI searches'}
                  </p>
                </div>
              ) : (
                <div className="score-loading">
                  <div className="loading-spinner"></div>
                  <p>Calculating...</p>
                </div>
              )}
            </div>

            {/* Right Column - Platform Breakdown */}
            <div className="platform-section">
              <h2>Platform Breakdown</h2>
              <div className="platforms-list">
                {sortPlatformResults(platformResults).map((result, index) => (
                  <div key={result.platform} className={`platform-item ${currentPlatform === result.platform ? 'active' : ''}`}>
                    <div className="platform-header" onClick={() => togglePlatformDetails(result.platform)}>
                      <div className="platform-info">
                        <div className="platform-icon">
                          {/* Platform icons based on name */}
                          {result.platform === 'openai' && <span className="platform-logo"><img src="/OpenAI.svg" alt="OpenAI" /></span>}
                          {result.platform === 'gemini' && <span className="platform-logo"><img src="/Gemini.svg" alt="Gemini" /></span>}
                          {result.platform === 'claude' && <span className="platform-logo"><img src="/Claude.svg" alt="Claude" /></span>}
                          {result.platform === 'grok' && <span className="platform-logo"><img src="/Grok.svg" alt="Grok" /></span>}
                          {result.platform === 'perplexity' && <span className="platform-logo"><img src="/Perplexity.svg" alt="Perplexity" /></span>}
                        </div>
                      </div>
                      <div className="platform-right">
                        <span 
                          className="platform-status"
                          style={{ color: getPlatformStatusColor(result) }}
                        >
                          {getPlatformStatus(result)}
                        </span>
                        <button className="expand-btn">
                          {expandedPlatforms.has(result.platform) ? '▲' : '▼'}
                        </button>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {expandedPlatforms.has(result.platform) && (
                      <div className="platform-details">
                        {/* Show query details for both completed AND running platforms (to show real-time results) */}
                        {((result.status === 'completed' || result.status === 'running') && result.queries && result.queries.length > 0) ? (
                          <div className="query-details">
                            {/* All queries breakdown */}
                            <div className="all-queries-section">
                              {/* Show running status if still in progress */}
                              {result.status === 'running' && (
                                <div className="running-queries-info">
                                  <div className="loading-spinner small"></div>
                                  <span>
                                    Analysis in progress: {result.queries.length} of {result.totalQueries || 0} queries completed
                                  </span>
                                </div>
                              )}
                              
                              <div className="queries-list">
                                {result.queries.map((queryResult, idx) => (
                                  <div key={idx} className={`query-item ${queryResult.found ? 'found' : 'not-found'}`}>
                                    {/* Query Title with Status Tag */}
                                    <div className="query-title-header">
                                      <h4 className="query-title">
                                        🔍 {queryResult.query.query_title}
                                      </h4>
                                      <div className="query-status-tag">
                                        {queryResult.found ? (
                                          <span className="status-found-tag">
                                            Found
                                          </span>
                                        ) : (
                                          <span className="status-not-found-tag">
                                            Not Found
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Confidence Bar */}
                                    <div className="confidence-bar">
                                      <div className="confidence-label">
                                        Query Confidence: {Math.round((queryResult.confidence || 0) * 100)}%
                                      </div>
                                      <div className="confidence-progress">
                                        <div 
                                          className="confidence-fill" 
                                          style={{ width: `${(queryResult.confidence || 0) * 100}%` }}
                                        ></div>
                                      </div>
                                    </div>

                                    {/* Query Section */}
                                    <div className="query-details">
                                      <h5>📝 Query</h5>
                                      <p className="processed-query">
                                        {replaceTemplateVariables(queryResult.query.query_text, formData)}
                                      </p>
                                    </div>

                                    {/* Response Section */}
                                    <div className="response-details">
                                      <h5>📄 Response</h5>
                                      {queryResult.result ? (
                                        <div className="response-content">
                                          <p>{queryResult.result.raw_response}</p>
                                          {queryResult.rank && (
                                            <div className="rank-info">
                                              <small>Rank: #{queryResult.rank}</small>
                                            </div>
                                          )}
                                        </div>
                                      ) : queryResult.error ? (
                                        <div className="error-content">
                                          <p>Error: {queryResult.error}</p>
                                        </div>
                                      ) : (
                                        <div className="not-found-content">
                                          <p>No response available</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : result.status === 'error' ? (
                          <div className="error-details">
                            <p>❌ Analysis failed: {result.error}</p>
                          </div>
                        ) : result.status === 'running' && (!result.queries || result.queries.length === 0) ? (
                          <div className="loading-details">
                            <div className="running-queries-breakdown">
                              <div className="overall-progress">
                                <div className="loading-spinner small"></div>
                                <span>Starting analysis on {result.totalQueries || 0} queries...</span>
                              </div>
                              
                              {/* Show individual query progress */}
                              {runningQueries[result.platform] && runningQueries[result.platform].length > 0 && (
                                <div className="individual-queries">
                                  <h4>Processing Queries:</h4>
                                  <div className="query-progress-list">
                                    {runningQueries[result.platform].map((queryId) => {
                                      // Find the query object to get the title
                                      const query = queries.find(q => q.query_id === queryId);
                                      return (
                                        <div key={queryId} className="query-progress-item">
                                          <div className="loading-spinner tiny"></div>
                                          <span className="query-title">
                                            {query?.query_title || queryId}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              
                              {/* Show completed queries */}
                              {result.totalQueries && runningQueries[result.platform] && (
                                <div className="progress-summary">
                                  <small>
                                    {(result.totalQueries - runningQueries[result.platform].length)} of {result.totalQueries} completed
                                  </small>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="loading-details">
                            <span>Waiting to start analysis...</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Section - Analysis Summary */}
          {isComplete && businessInfo && (
            <div className="analysis-summary">
              <h2>Analysis Summary</h2>
              <p>
                {businessInfo.name} currently has limited visibility in AI-driven search results, with an overall score of {overallScore}/10. 
                While the business performs well on ChatGPT, consistently appearing in top positions for {formData.service} services in {formData.city}, 
                it lacks presence on other platforms like Perplexity, Grok, and Claude. This limited visibility could hinder potential 
                customer acquisition and brand recognition across diverse AI search platforms. Air Pro USA should consider optimizing their 
                digital content for broader AI compatibility and investing in targeted SEO strategies to improve rankings across multiple AI search engines.
              </p>

              <div className="query-stats">
                <h3>Search Query Statistics</h3>
                <div className="stats-row">
                  <div className="stat-box">
                    <div className="stat-number">
                      {platformResults.reduce((sum, result) => sum + (result.totalQueries || 0), 0)}
                    </div>
                    <div className="stat-label">
                      out of {platformResults.reduce((sum, result) => sum + (result.totalQueries || 0), 0)}
                    </div>
                    <div className="stat-description">Total Queries Run</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-number">
                      {(() => {
                        const totalQueries = platformResults.reduce((sum, result) => sum + (result.totalQueries || 0), 0);
                        const successfulFinds = platformResults.reduce((sum, result) => sum + (result.foundCount || 0), 0);
                        return totalQueries > 0 ? Math.round((successfulFinds / totalQueries) * 100) : 0;
                      })()}%
                    </div>
                    <div className="stat-label">
                      {platformResults.reduce((sum, result) => sum + (result.foundCount || 0), 0)} successful mentions
                    </div>
                    <div className="stat-description">Business Found Rate</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-number">
                      #{(() => {
                        const allRanks = platformResults
                          .filter(r => r.queries)
                          .flatMap(r => r.queries!)
                          .filter(q => q.found && q.rank)
                          .map(q => q.rank!);
                        return allRanks.length > 0 ? 
                          (allRanks.reduce((sum, rank) => sum + rank, 0) / allRanks.length).toFixed(1) : 
                          'N/A';
                      })()}
                    </div>
                    <div className="stat-label">Lower numbers are better</div>
                    <div className="stat-description">Avg. Position When Found</div>
                  </div>
                </div>
              </div>

              <div className="query-type-summary">
                <h3>Query Type Summary</h3>
                <div className="query-type-list">
                  {(() => {
                    // Group queries by type and calculate stats
                    const queryTypeStats = queries.reduce((acc, query) => {
                      const queryType = query.query_title || 'Unknown';
                      if (!acc[queryType]) {
                        acc[queryType] = { total: 0, found: 0 };
                      }
                      acc[queryType].total += 1;
                      
                      // Check if this query was found in any platform result
                      platformResults.forEach(platformResult => {
                        if (platformResult.queries) {
                          const queryResult = platformResult.queries.find(qr => qr.query.query_id === query.query_id);
                          if (queryResult && queryResult.found) {
                            acc[queryType].found += 1;
                          }
                        }
                      });
                      
                      return acc;
                    }, {} as Record<string, { total: number; found: number }>);

                    return Object.entries(queryTypeStats).map(([queryType, stats]) => {
                      const successRate = stats.total > 0 ? Math.round((stats.found / stats.total) * 100) : 0;
                      
                      return (
                        <div key={queryType} className="query-type-row">
                          <div className="query-type-info">
                            <span className="query-type-name">{queryType}:</span>
                            <span className="query-type-stats">
                              {stats.found} found
                            </span>
                          </div>
                          <div className="query-type-progress">
                            <div className="progress-bar">
                              <div 
                                className="progress-fill" 
                                style={{ 
                                  width: `${successRate}%`,
                                  backgroundColor: successRate > 0 ? '#ef4444' : '#e5e7eb'
                                }}
                              ></div>
                            </div>
                            <span className="success-rate">{successRate}% success rate</span>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalysisResults;